/**
 * CMS Content Hook - Fetches content from CMS with fallback to defaults
 * Usage: const { content, loading } = useCMS('home');
 * Access: content('hero.title', 'Default Title')
 */
import { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

// Cache for CMS content to avoid repeated fetches
const contentCache = {};

/**
 * Fetch CMS content for a page
 */
export async function fetchCMSContent(pageId) {
  if (contentCache[pageId]) {
    return contentCache[pageId];
  }
  
  try {
    const res = await fetch(`${API}/api/cms/content/${pageId}`);
    if (res.ok) {
      const data = await res.json();
      contentCache[pageId] = data.sections || {};
      return contentCache[pageId];
    }
  } catch (err) {
    console.warn(`CMS fetch failed for ${pageId}:`, err);
  }
  
  return {};
}

/**
 * Clear cache (useful after CMS updates)
 */
export function clearCMSCache(pageId = null) {
  if (pageId) {
    delete contentCache[pageId];
  } else {
    Object.keys(contentCache).forEach(key => delete contentCache[key]);
  }
}

/**
 * Hook to use CMS content in a component
 * @param {string} pageId - The CMS page ID (home, pricing, corporate, etc.)
 * @returns {{ content: Function, loading: boolean, refresh: Function }}
 */
export function useCMS(pageId) {
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    
    fetchCMSContent(pageId).then(data => {
      if (mounted) {
        setSections(data);
        setLoading(false);
      }
    });
    
    return () => { mounted = false; };
  }, [pageId]);

  /**
   * Get content by key with fallback
   * @param {string} key - Dot notation key like "hero.title" or "stats.stat1_value"
   * @param {string} fallback - Default value if CMS content is empty
   * @returns {string} The content value or fallback
   */
  const content = useCallback((key, fallback = '') => {
    const parts = key.split('.');
    let value = sections;
    
    for (const part of parts) {
      if (value && typeof value === 'object' && part in value) {
        value = value[part];
      } else {
        return fallback;
      }
    }
    
    // Return fallback if value is empty string or null
    return value && value.toString().trim() !== '' ? value : fallback;
  }, [sections]);

  /**
   * Refresh content from CMS
   */
  const refresh = useCallback(async () => {
    delete contentCache[pageId];
    setLoading(true);
    const data = await fetchCMSContent(pageId);
    setSections(data);
    setLoading(false);
  }, [pageId]);

  return { content, loading, refresh, sections };
}

/**
 * Helper to get image URL with fallback
 * @param {string} cmsUrl - URL from CMS
 * @param {string} fallbackUrl - Default image URL
 * @returns {string} The image URL
 */
export function getImageUrl(cmsUrl, fallbackUrl = '') {
  if (cmsUrl && cmsUrl.trim() !== '') {
    // If it's a relative URL from our uploads, keep it
    // If it's already absolute, use as-is
    return cmsUrl;
  }
  return fallbackUrl;
}

export default useCMS;
